/*
 * av-buybox.js — Avanelle native buy box controller (WP1)
 * -----------------------------------------------------------------------------
 * Externalized, idempotent replacement for the section's former inline <script>
 * IIFEs. Loaded once (defer) and persists across no-reload flavor swaps.
 *
 *  - AvBuyBox.mount(root)     wires one #av-buybox instance (gallery + zoom, tier
 *                            select, autoship toggle, ATC, sticky bar, nutrition
 *                            lightbox, bfcache reset). Idempotent — guarded by
 *                            root.dataset.avMounted; safe to call after each swap.
 *  - AvBuyBox.teardown(root)  resets page-level side effects (body overflow /
 *                            paddingBottom) a swap could otherwise strand.
 *
 * Page-level listeners (Escape, gallery arrow-keys, offer-CTA smooth scroll,
 * sticky scroll/resize, bfcache pageshow) are bound ONCE and always resolve the
 * CURRENT #av-buybox at event time — so swapping the node never accumulates
 * duplicate handlers or holds stale references (audit R1/R2/R3).
 *
 * Per-product values (locale format strings, routes, ids, price) are read from
 * the <script type="application/json" data-av-config> island inside each root,
 * so a Section-Rendering-API-swapped sibling carries its own config.
 */
(function () {
  'use strict';
  if (window.AvBuyBox) return;

  var GLOBALS_BOUND = false;
  var _lastFocus = null;

  function cfgOf(root) {
    var el = root && root.querySelector('[data-av-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }
  function currentRoot() { return document.getElementById('av-buybox'); }

  /* ------------------------------- focus trap ------------------------------ */
  /* Only one modal (gallery OR nutrition) is ever open at a time, so a single
     module-level saved-focus is sufficient. */
  function trapFocus(modal) {
    _lastFocus = document.activeElement;
    var f = modal.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
    if (f.length) f[0].focus();
    modal.__trap = function (e) {
      if (e.key !== 'Tab' || !f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    modal.addEventListener('keydown', modal.__trap);
  }
  function releaseFocus(modal) {
    if (modal && modal.__trap) { modal.removeEventListener('keydown', modal.__trap); modal.__trap = null; }
    if (_lastFocus && _lastFocus.focus) _lastFocus.focus();
    _lastFocus = null;
  }

  /* ------------------------- gallery + zoom lightbox ----------------------- */
  function mountGallery(root, cfg) {
    var COUNTER_FMT = cfg.counterFmt || '{{ current }} / {{ total }}';
    function fmtCounter(cur, tot) { return String(COUNTER_FMT).replace('__CUR__', cur).replace('__TOT__', tot); }

    var imgs = [].slice.call(root.querySelectorAll('.av-gallery__img'));
    var thumbs = [].slice.call(root.querySelectorAll('.av-gallery__thumb'));
    var counter = root.querySelector('[data-gallery-counter]');
    var idx = 0;
    function show(n) {
      if (!imgs.length) return;
      idx = (n + imgs.length) % imgs.length;
      imgs.forEach(function (im, i) { im.classList.toggle('is-active', i === idx); });
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
      if (counter) counter.textContent = fmtCounter(idx + 1, imgs.length);
      var at = thumbs[idx];
      if (at && at.scrollIntoView) at.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    thumbs.forEach(function (t) { t.addEventListener('click', function () { show(+t.dataset.idx); }); });
    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { show(idx - 1); });
    if (next) next.addEventListener('click', function () { show(idx + 1); });

    /* swipe on the main stage */
    var stage = root.querySelector('[data-gallery-stage]');
    if (stage) {
      var x0 = null;
      stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
        x0 = null;
      }, { passive: true });
    }

    /* click-to-open lightbox with real pan/zoom */
    var gModal = root.querySelector('[data-gallery-modal]');
    var gZoom = root.querySelector('[data-gallery-zoom-img]');
    var gCounter = root.querySelector('[data-gallery-modal-counter]');
    var zs = 1, ztx = 0, zty = 0;
    function applyZoom() { if (gZoom) { gZoom.style.transform = 'translate(' + ztx + 'px,' + zty + 'px) scale(' + zs + ')'; gZoom.style.cursor = zs > 1 ? 'grab' : 'zoom-in'; } }
    function resetZoom() { zs = 1; ztx = 0; zty = 0; if (gZoom) gZoom.style.transition = 'none'; applyZoom(); }
    function clampPan() { if (!gZoom) return; var mx = gZoom.clientWidth * (zs - 1) / 2, my = gZoom.clientHeight * (zs - 1) / 2; ztx = Math.max(-mx, Math.min(mx, ztx)); zty = Math.max(-my, Math.min(my, zty)); }
    function zoomAt(factor, cx, cy) {
      if (!gZoom) return;
      var r = gZoom.getBoundingClientRect();
      var ox = cx - (r.left + r.width / 2), oy = cy - (r.top + r.height / 2);
      var ns = Math.max(1, Math.min(4, zs * factor)); var k = ns / zs;
      ztx = ox - (ox - ztx) * k; zty = oy - (oy - zty) * k; zs = ns;
      if (zs === 1) { ztx = 0; zty = 0; } clampPan();
      if (gZoom) gZoom.style.transition = 'transform .12s ease'; applyZoom();
    }
    function closeG() { if (gModal) { gModal.hidden = true; document.body.style.overflow = ''; resetZoom(); releaseFocus(gModal); } }
    function syncModalImage() { var a = imgs[idx]; if (a && gZoom) gZoom.src = a.currentSrc || a.src; resetZoom(); if (gCounter) gCounter.textContent = fmtCounter(idx + 1, imgs.length); }
    function modalNav(d) { show(idx + d); syncModalImage(); }
    function openG() { if (!imgs.length || !gModal || !gZoom) return; gModal.hidden = false; document.body.style.overflow = 'hidden'; syncModalImage(); trapFocus(gModal); }

    if (stage && gModal && gZoom) {
      stage.addEventListener('click', openG);
      stage.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openG(); } });
      gModal.querySelectorAll('[data-gallery-close]').forEach(function (b) { b.addEventListener('click', closeG); });
      var gmPrev = gModal.querySelector('[data-gallery-modal-prev]');
      var gmNext = gModal.querySelector('[data-gallery-modal-next]');
      if (gmPrev) gmPrev.addEventListener('click', function (e) { e.stopPropagation(); modalNav(-1); });
      if (gmNext) gmNext.addEventListener('click', function (e) { e.stopPropagation(); modalNav(1); });
      var _swx = null;
      gZoom.addEventListener('touchstart', function (e) { _swx = (zs === 1 && e.touches.length === 1) ? e.touches[0].clientX : null; }, { passive: true });
      gZoom.addEventListener('touchend', function (e) { if (_swx === null || zs !== 1) { _swx = null; return; } var dx = e.changedTouches[0].clientX - _swx; if (Math.abs(dx) > 50) modalNav(dx < 0 ? 1 : -1); _swx = null; }, { passive: true });
      gZoom.addEventListener('wheel', function (e) { e.preventDefault(); zoomAt(e.deltaY < 0 ? 1.18 : 0.85, e.clientX, e.clientY); }, { passive: false });
      gZoom.addEventListener('dblclick', function (e) { e.preventDefault(); if (zs > 1) resetZoom(); else zoomAt(2.5, e.clientX, e.clientY); });
      var dragging = false, lx = 0, ly = 0, pts = {}, pinchD = 0;
      gZoom.addEventListener('pointerdown', function (e) {
        pts[e.pointerId] = { x: e.clientX, y: e.clientY }; var n = Object.keys(pts).length;
        if (n === 1) { dragging = zs > 1; lx = e.clientX; ly = e.clientY; try { gZoom.setPointerCapture(e.pointerId); } catch (_) {} }
        else if (n === 2) { dragging = false; var p = Object.keys(pts).map(function (k) { return pts[k]; }); pinchD = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y); }
      });
      gZoom.addEventListener('pointermove', function (e) {
        if (pts[e.pointerId]) pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var keys = Object.keys(pts);
        if (keys.length === 2) { var p = keys.map(function (k) { return pts[k]; }); var d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y); if (pinchD) zoomAt(d / pinchD, (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2); pinchD = d; }
        else if (dragging) { ztx += e.clientX - lx; zty += e.clientY - ly; lx = e.clientX; ly = e.clientY; clampPan(); if (gZoom) gZoom.style.transition = 'none'; applyZoom(); }
      });
      function endPtr(e) { delete pts[e.pointerId]; if (Object.keys(pts).length < 2) pinchD = 0; if (!Object.keys(pts).length) dragging = false; }
      gZoom.addEventListener('pointerup', endPtr);
      gZoom.addEventListener('pointercancel', endPtr);
    }

    root.__avGallery = {
      closeG: closeG,
      modalNav: modalNav,
      galleryModalOpen: function () { return !!(gModal && !gModal.hidden); }
    };
  }

  /* ---------------------------- nutrition lightbox ------------------------- */
  function mountNutrition(root) {
    var nModal = root.querySelector('[data-nutrition-modal]');
    function closeN() { if (nModal) { nModal.hidden = true; document.body.style.overflow = ''; releaseFocus(nModal); } }
    if (nModal) {
      root.querySelectorAll('[data-nutrition-open]').forEach(function (b) {
        b.addEventListener('click', function () { nModal.hidden = false; document.body.style.overflow = 'hidden'; trapFocus(nModal); });
      });
      nModal.querySelectorAll('[data-nutrition-close]').forEach(function (b) { b.addEventListener('click', closeN); });
    }
    root.__avNutrition = { closeN: closeN };
  }

  /* ------------------------- tier select + ATC + OTP ------------------------ */
  function mountOffer(root, cfg) {
    var bundle = root.querySelector('[data-av-bundle]');
    if (!bundle) { root.__avOffer = null; return; }
    var OTP = cfg.otp || {};
    var ROOT_URL = cfg.rootUrl || '/';
    var CART_ADD_URL = cfg.cartAddUrl || '/cart/add.js';
    var CHECKOUT_URL = (ROOT_URL === '/' ? '' : ROOT_URL) + '/checkout';
    var variantId = bundle.getAttribute('data-variant');

    var rows = [].slice.call(bundle.querySelectorAll('.av-bundle__row'));

    /* Content-aware compaction (see section CSS `.av-bundle.is-compact`).
       The row can distort only when its single-line content doesn't fit — which happens
       for long-digit currencies (e.g. MXN "$ 2,566" / "$ 5,345") on narrow viewports, not
       at any fixed width. We measure the actual rendered layout with images shown; if a
       title is forced onto a second line, we hide the decorative bottle images so the
       price + title stay on one line. Short-price markets never trip it. Idempotent:
       every call re-measures from the roomy state, so it settles without oscillating. */
    function rowWraps(row) {
      var t = row.querySelector('.av-bundle__title');
      if (!t) return false;
      var lh = parseFloat(getComputedStyle(t).lineHeight);
      if (!lh || isNaN(lh)) lh = parseFloat(getComputedStyle(t).fontSize) * 1.25;
      return t.getBoundingClientRect().height > lh * 1.6; /* > ~1 line */
    }
    function fitBundle() {
      bundle.classList.remove('is-compact');       /* measure the roomy layout */
      var tight = false;
      for (var i = 0; i < rows.length; i++) { if (rowWraps(rows[i])) { tight = true; break; } }
      bundle.classList.toggle('is-compact', tight);
    }

    function currentRow() { return bundle.querySelector('.av-bundle__row.is-selected') || rows[0]; }

    /* -------------------------- OTP text link sync -------------------------- */
    var otpLink = root.querySelector('[data-otp-link]');
    var otpLabelText = null, otpErrorEl = root.querySelector('[data-otp-error]');
    var otpWasEl, otpNowEl, otpDayEl, otpLabelEl;
    if (otpLink) {
      otpLabelEl = otpLink.querySelector('[data-otp-label]');
      otpWasEl = otpLink.querySelector('[data-otp-was]');
      otpNowEl = otpLink.querySelector('[data-otp-now]');
      otpDayEl = otpLink.querySelector('[data-otp-day]');
      otpLabelText = otpLabelEl ? otpLabelEl.textContent : '';
    }
    function syncOtp() {
      if (!otpLink) return;
      var row = currentRow();
      if (!row) return;
      var wasSrc = row.querySelector('.av-bundle__was');
      var nowSrc = row.querySelector('.av-bundle__now');
      var daySrc = row.querySelector('.av-bundle__sub');
      var saveSrc = row.querySelector('.av-bundle__save');
      var save = saveSrc ? saveSrc.getAttribute('data-save-otp') : '0';
      var hasDiscount = !!(save && save !== '0');
      if (otpWasEl) { otpWasEl.textContent = wasSrc ? wasSrc.textContent.trim() : ''; otpWasEl.hidden = !hasDiscount; }
      if (otpNowEl) otpNowEl.textContent = nowSrc ? nowSrc.getAttribute('data-now-otp') : '';
      if (otpDayEl) {
        var day = daySrc ? daySrc.getAttribute('data-day-otp') : '';
        otpDayEl.textContent = day ? String(OTP.perDayFmt || '__PRICE__').replace('__PRICE__', day) : '';
      }
    }

    /* ------------------------------- tier select ------------------------------ */
    function select(row) {
      rows.forEach(function (r) {
        var on = r === row;
        r.classList.toggle('is-selected', on);
        r.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      syncOtp();
    }
    rows.forEach(function (r) {
      r.addEventListener('click', function () { select(r); });
      r.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(r); } });
    });

    /* --------------------------- shared cart-add POST -------------------------- */
    /* Builds the selected tier's paid (+free) line items. subscribe=true (main ATC,
       tiers are subscription-only) attaches selling_plan; subscribe=false (OTP link)
       omits it — everything else about the payload is identical (L3). */
    function buildItems(row, subscribe) {
      var paid = parseInt(row.getAttribute('data-paid'), 10) || 1;
      var free = parseInt(row.getAttribute('data-free'), 10) || 0;
      var plan = row.getAttribute('data-plan') || '';
      var titleEl = row.querySelector('.av-bundle__title');
      var offer = titleEl ? titleEl.textContent.trim() : 'Bundle';
      var bundleId = 'av' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
      var items = [];
      var paidItem = { id: Number(variantId), quantity: paid, properties: { _av_bundle: offer, _av_bundle_id: bundleId, _av_plan: plan } };
      if (subscribe && plan) paidItem.selling_plan = Number(plan);
      items.push(paidItem);
      if (free > 0) items.push({ id: Number(variantId), quantity: free, properties: { _av_bundle: offer, _av_bundle_id: bundleId, _av_free: 'true' } });
      return items;
    }
    function submitAdd(items, btn, onError) {
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      return fetch(CART_ADD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) { btn.disabled = false; btn.removeAttribute('aria-busy'); onError(res.description || res.message); return; }
          window.location.href = CHECKOUT_URL;
        })
        .catch(function (err) { btn.disabled = false; btn.removeAttribute('aria-busy'); onError(err); });
    }

    /* Real Add to Cart — subscribes the selected tier (tiers are subscription-only). */
    var atcBtn = root.querySelector('[data-bundle-atc]');
    if (atcBtn && variantId) {
      atcBtn.addEventListener('click', function () {
        var row = currentRow();
        if (!row) return;
        submitAdd(buildItems(row, true), atcBtn, function (err) { console.warn('[av-bundle] add error:', err); });
      });
    }

    /* One-Time Purchase text link — same payload as the button minus selling_plan (L3),
       same checkout redirect (L4), disables + shows a loading label while in flight (G5),
       re-enables with an inline localized error on failure without auto-retry. */
    if (otpLink && variantId) {
      otpLink.addEventListener('click', function () {
        var row = currentRow();
        if (!row) return;
        if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
        if (otpLabelEl && OTP.adding) otpLabelEl.textContent = OTP.adding;
        submitAdd(buildItems(row, false), otpLink, function () {
          if (otpLabelEl) otpLabelEl.textContent = otpLabelText;
          if (otpErrorEl) { otpErrorEl.textContent = OTP.error || ''; otpErrorEl.hidden = false; }
        });
      });
    }

    /* Back-nav / bfcache recovery for the OTP button. The click handler above leaves it
       disabled with the "Adding…" label because the tab is on its way to /checkout; when
       Safari restores this page from bfcache (back button) that in-flight state comes back
       with it and reads as a stuck spinner. Called from __avBfcache on every pageshow —
       a no-op when the button is already at rest. */
    function resetOtp() {
      if (!otpLink) return;
      otpLink.disabled = false;
      otpLink.removeAttribute('aria-busy');
      if (otpLabelEl && otpLabelText != null) otpLabelEl.textContent = otpLabelText;
      if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
    }

    /* Selection carry-over across a flavor swap (paid quantity only — tiers no longer
       have a subscribe/one-time mode to carry). */
    root.__avOffer = {
      fit: fitBundle,
      resetOtp: resetOtp,
      getSelection: function () {
        var r = bundle.querySelector('.av-bundle__row.is-selected');
        return { paid: r ? r.getAttribute('data-paid') : null };
      },
      applySelection: function (sel) {
        if (!sel) return;
        if (sel.paid != null) {
          var target = bundle.querySelector('.av-bundle__row[data-paid="' + sel.paid + '"]');
          if (target) select(target);
        }
      }
    };

    syncOtp();    /* initial paint already server-renders tier 2's OTP price; this just wires it up */
    fitBundle();  /* initial pass; re-run once the webfont settles (metrics shift wrapping) */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitBundle);
  }

  /* ------------------------------ sticky ATC ------------------------------- */
  function mountSticky(root) {
    var sticky = root.querySelector('[data-av-sticky]');
    var bundle = root.querySelector('[data-av-bundle]');
    var atcMain = root.querySelector('[data-bundle-atc]');
    if (!sticky || !bundle) { root.__avSticky = null; return; }
    var rows = [].slice.call(bundle.querySelectorAll('.av-bundle__row'));
    var offerEl = sticky.querySelector('[data-sticky-offer]');
    var nowEl = sticky.querySelector('[data-sticky-now]');
    var wasEl = sticky.querySelector('[data-sticky-was]');
    function barH() { return sticky.offsetHeight || 64; }
    function reserve() { document.body.style.paddingBottom = barH() + 'px'; }
    function syncSticky() {
      var row = bundle.querySelector('.av-bundle__row.is-selected') || rows[0];
      if (!row) return;
      var t = row.querySelector('.av-bundle__title');
      var now = row.querySelector('.av-bundle__now');
      var was = row.querySelector('.av-bundle__was');
      if (offerEl && t) offerEl.textContent = t.textContent.trim();
      if (nowEl && now) nowEl.textContent = now.textContent.trim();
      if (wasEl) {
        var showWas = was && was.style.display !== 'none' && was.textContent.trim();
        wasEl.textContent = showWas ? was.textContent.trim() : '';
        wasEl.style.display = showWas ? '' : 'none';
      }
    }
    function place() {
      var visible = atcMain ? (atcMain.getBoundingClientRect().bottom < 0) : false;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    /* Re-sync after any tier / subscribe interaction (per-root, dies with the node). */
    root.addEventListener('click', function (e) { if (e.target.closest('.av-bundle__row')) requestAnimationFrame(syncSticky); });
    root.addEventListener('keyup', function (e) { if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('.av-bundle__row')) requestAnimationFrame(syncSticky); });
    var stickyBtn = sticky.querySelector('[data-sticky-atc]');
    if (stickyBtn && atcMain) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled || atcMain.disabled) return;
        stickyBtn.disabled = true;
        atcMain.click();
        setTimeout(function () { stickyBtn.disabled = false; }, 1500);
      });
    }
    reserve(); syncSticky(); place();
    root.__avSticky = { place: place, reserve: reserve, syncSticky: syncSticky };
  }

  /* --------------------------- bfcache recovery ---------------------------- */
  function mountBfcache(root) {
    var atc = root.querySelector('[data-bundle-atc]');
    var sticky = root.querySelector('[data-sticky-atc]');
    /* Resting disabled state = server render (true only when sold out). */
    var atcResting = atc ? atc.disabled : false;
    var stickyResting = sticky ? sticky.disabled : false;
    root.__avBfcache = function (e) {
      if (atc) { atc.disabled = atcResting; atc.removeAttribute('aria-busy'); }
      if (sticky) { sticky.disabled = stickyResting; sticky.removeAttribute('aria-busy'); }
      /* Resolved at call time so a flavor swap's fresh __avOffer is the one reset. */
      if (root.__avOffer && root.__avOffer.resetOtp) root.__avOffer.resetOtp();
      if (e && e.persisted) {
        var cd = document.querySelector('cart-drawer');
        if (cd && typeof cd.refreshCartDrawer === 'function') cd.refreshCartDrawer();
      }
    };
  }

  /* ------------------- offer-CTA smooth scroll (delegated) ----------------- */
  function topOffset() {
    var bottom = 0;
    document.querySelectorAll('header, sticky-header, [class*="header"], [class*="announcement"]').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        var r = el.getBoundingClientRect();
        if (r.top <= 2 && r.height > 8 && r.height < 240 && r.bottom > bottom) bottom = r.bottom;
      }
    });
    return bottom > 0 ? bottom + 12 : 16;
  }
  function glide(toY, dur) {
    var startY = window.pageYOffset, diff = toY - startY, t0 = null;
    function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
    function step(ts) { if (t0 === null) t0 = ts; var p = Math.min((ts - t0) / dur, 1); window.scrollTo(0, startY + diff * ease(p)); if (p < 1) requestAnimationFrame(step); }
    requestAnimationFrame(step);
  }
  function offerCtaHandler(e) {
    var a = e.target.closest('a[href$="#av-buybox"], a[href$="#av-offer"]');
    if (!a) return;
    var target = document.getElementById('av-offer') || document.querySelector('.av-bundle') || document.getElementById('av-buybox');
    if (!target) return;
    e.preventDefault();
    var y = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - topOffset());
    var dist = Math.abs(y - window.pageYOffset);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || dist < 4) { window.scrollTo(0, y); return; }
    glide(y, Math.max(320, Math.min(640, dist * 0.45)));
  }

  /* --------------------------- global listeners ---------------------------- */
  function bindGlobalsOnce() {
    if (GLOBALS_BOUND) return;
    GLOBALS_BOUND = true;
    document.addEventListener('keydown', function (e) {
      var root = currentRoot();
      if (!root) return;
      if (e.key === 'Escape') {
        if (root.__avGallery) root.__avGallery.closeG();
        if (root.__avNutrition) root.__avNutrition.closeN();
        return;
      }
      var g = root.__avGallery;
      if (g && g.galleryModalOpen()) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); g.modalNav(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); g.modalNav(1); }
      }
    });
    document.addEventListener('click', offerCtaHandler, false);
    window.addEventListener('scroll', function () { var r = currentRoot(); if (r && r.__avSticky) r.__avSticky.place(); }, { passive: true });
    window.addEventListener('resize', function () { var r = currentRoot(); if (!r) return; if (r.__avSticky) { r.__avSticky.reserve(); r.__avSticky.place(); } if (r.__avOffer && r.__avOffer.fit) r.__avOffer.fit(); }, { passive: true });
    window.addEventListener('pageshow', function (e) { var r = currentRoot(); if (r && r.__avBfcache) r.__avBfcache(e); });
  }

  /* -------------------------------- mount ---------------------------------- */
  function mount(root) {
    if (!root || root.dataset.avMounted === '1') return;
    root.dataset.avMounted = '1';
    var cfg = cfgOf(root);
    mountGallery(root, cfg);
    mountNutrition(root);
    mountOffer(root, cfg);
    mountSticky(root);
    mountBfcache(root);
    bindGlobalsOnce();
  }
  function teardown(root) {
    document.body.style.overflow = '';
    document.body.style.paddingBottom = '';
    _lastFocus = null;
    if (root) root.dataset.avMounted = '';
  }

  window.AvBuyBox = { mount: mount, teardown: teardown, config: cfgOf };

  function boot() { [].slice.call(document.querySelectorAll('[data-bb]')).forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
